import type {
  BillingPricingRule,
  BillingPricingRuleCondition,
  RuleOperator,
  ServiceComboMatchMode,
} from '../../apps/control-room/services/billingTypes';

type Primitive = string | number | boolean | null | undefined;

export interface BillingEvaluationContext {
  organisation_id?: string | null;
  metric: BillingPricingRule['metric'];
  values: Record<string, Primitive>;
  enabledServices?: string[];
  timestamp?: string;
  /**
   * When true, rule validity window (valid_from/valid_to) is ignored so that rules created
   * later can apply to past invoice periods. Use for usage settlement so you can create
   * rules and then generate older invoices.
   */
  applyToPastPeriods?: boolean;
}

export interface BillingRuleMatchResult {
  matchedRule: BillingPricingRule | null;
  matchedConditions: BillingPricingRuleCondition[];
}

const OPERATOR_EVALUATORS: Record<RuleOperator, (left: Primitive, right: Primitive) => boolean> = {
  '=': (left, right) => String(left) === String(right),
  '!=': (left, right) => String(left) !== String(right),
  '<': (left, right) => Number(left) < Number(right),
  '<=': (left, right) => Number(left) <= Number(right),
  '>': (left, right) => Number(left) > Number(right),
  '>=': (left, right) => Number(left) >= Number(right),
};

function isWithinValidityWindow(rule: BillingPricingRule, timestampISO: string): boolean {
  const at = new Date(timestampISO).getTime();
  const from = new Date(rule.valid_from).getTime();
  const to = rule.valid_to ? new Date(rule.valid_to).getTime() : Number.POSITIVE_INFINITY;

  return at >= from && at <= to;
}

/** For video_minutes rules: threshold from max_players_in_starting_lineup <= N (so 8v8 before 11v11). */
function getMaxPlayersThreshold(
  conditions: BillingPricingRuleCondition[]
): number {
  const key = 'max_players_in_starting_lineup';
  const legacyKey = 'players_in_starting_lineup';
  for (const c of conditions) {
    if ((c.field_key === key || c.field_key === legacyKey) && c.operator === '<=') {
      const n = Number(c.field_value);
      if (!Number.isNaN(n)) return n;
    }
  }
  return Number.POSITIVE_INFINITY;
}

function matchServiceCombo(
  mode: ServiceComboMatchMode,
  expectedServices: string[],
  enabledServices: string[]
): boolean {
  const expected = new Set(expectedServices);
  const enabled = new Set(enabledServices);

  if (mode === 'exact') {
    if (expected.size !== enabled.size) {
      return false;
    }
  }

  for (const value of expected) {
    if (!enabled.has(value)) {
      return false;
    }
  }

  return true;
}

function evaluateRuleConditions(
  conditions: BillingPricingRuleCondition[],
  values: Record<string, Primitive>
): boolean {
  if (conditions.length === 0) {
    return true;
  }

  return conditions.every((condition) => {
    const evaluator = OPERATOR_EVALUATORS[condition.operator];
    const left = values[condition.field_key];
    const right = condition.field_value;

    if (left === null || left === undefined) {
      return false;
    }

    return evaluator(left, right);
  });
}

export function selectMatchingBillingRule(params: {
  rules: BillingPricingRule[];
  conditionsByRuleId: Record<string, BillingPricingRuleCondition[]>;
  context: BillingEvaluationContext;
}): BillingRuleMatchResult {
  const { rules, conditionsByRuleId, context } = params;
  const at = context.timestamp || new Date().toISOString();
  const skipValidity = Boolean(context.applyToPastPeriods);

  const sortedCandidates = [...rules]
    .filter(
      (rule) =>
        rule.is_active &&
        rule.metric === context.metric &&
        (skipValidity || isWithinValidityWindow(rule, at))
    )
    .sort((a, b) => {
      const aScope = a.organisation_id ? 1 : 0;
      const bScope = b.organisation_id ? 1 : 0;
      if (aScope !== bScope) return bScope - aScope;
      if (a.priority !== b.priority) return a.priority - b.priority;
      // For video_minutes: try smaller threshold first (8v8 before 11v11)
      if (context.metric === 'video_minutes') {
        const ta = getMaxPlayersThreshold(conditionsByRuleId[a.id] || []);
        const tb = getMaxPlayersThreshold(conditionsByRuleId[b.id] || []);
        return ta - tb;
      }
      return 0;
    });

  for (const candidate of sortedCandidates) {
    if (candidate.organisation_id && candidate.organisation_id !== context.organisation_id) {
      continue;
    }

    if (candidate.metric === 'service_combo') {
      const expected = candidate.service_combo_services || [];
      const mode = candidate.service_combo_match_mode || 'contains';
      if (!matchServiceCombo(mode, expected, context.enabledServices || [])) {
        continue;
      }
    }

    const conditions = conditionsByRuleId[candidate.id] || [];
    if (!evaluateRuleConditions(conditions, context.values)) {
      continue;
    }

    return { matchedRule: candidate, matchedConditions: conditions };
  }

  return { matchedRule: null, matchedConditions: [] };
}

/**
 * Returns a short explanation of why no rule matched (for debugging / UI).
 * Call when selectMatchingBillingRule returned null but you expected a match.
 */
export function explainRuleMatchFailure(params: {
  rules: BillingPricingRule[];
  conditionsByRuleId: Record<string, BillingPricingRuleCondition[]>;
  context: BillingEvaluationContext;
}): string {
  const { rules, conditionsByRuleId, context } = params;
  const at = context.timestamp || new Date().toISOString();
  const skipValidity = Boolean(context.applyToPastPeriods);

  const candidates = [...rules]
    .filter(
      (rule) =>
        rule.is_active &&
        rule.metric === context.metric &&
        (skipValidity || isWithinValidityWindow(rule, at))
    )
    .sort((a, b) => {
      const aScope = a.organisation_id ? 1 : 0;
      const bScope = b.organisation_id ? 1 : 0;
      if (aScope !== bScope) return bScope - aScope;
      return a.priority - b.priority;
    });

  const parts: string[] = [];
  for (const rule of candidates) {
    if (rule.organisation_id && rule.organisation_id !== context.organisation_id) {
      parts.push(`${rule.name}: wrong organisation`);
      continue;
    }
    if (rule.metric === 'service_combo') {
      const expected = rule.service_combo_services || [];
      const mode = rule.service_combo_match_mode || 'contains';
      if (!matchServiceCombo(mode, expected, context.enabledServices || [])) {
        parts.push(`${rule.name}: service combo not met`);
      }
      continue;
    }
    const conditions = conditionsByRuleId[rule.id] || [];
    if (conditions.length === 0) {
      parts.push(`${rule.name}: matched (no conditions)`);
      continue;
    }
    const failed: string[] = [];
    for (const cond of conditions) {
      const left = context.values[cond.field_key];
      const right = cond.field_value;
      if (left === null || left === undefined) {
        failed.push(`${cond.field_key} missing`);
        continue;
      }
      const evaluator = OPERATOR_EVALUATORS[cond.operator];
      if (!evaluator(left, right)) {
        failed.push(`${cond.field_key} (${left}) fails ${cond.operator} ${right}`);
      }
    }
    if (failed.length > 0) {
      parts.push(`${rule.name}: ${failed.join('; ')}`);
    }
  }
  if (parts.length === 0) return 'No pricing rules for this metric (add a rule with this metric).';
  return parts.join('. ');
}
